import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllQuizAttemptsQuery } from "../../slices/quizAttemptApiSlice";
import { useGetStudentsQuery } from "../../slices/usersApiSlice";

const AdminQuizAttempts = () => {
  const navigate = useNavigate();

  const {
    data: attempts = [],
    isLoading,
    isError,
    error,
  } = useGetAllQuizAttemptsQuery();

 const { data: students = [] } = useGetStudentsQuery();

  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedResult, setSelectedResult] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [minPercentage, setMinPercentage] = useState("");
  const [maxPercentage, setMaxPercentage] = useState("");

const filteredAttempts = useMemo(() => {
    return attempts.filter((attempt) => {
      if (selectedStudent && attempt.student?._id !== selectedStudent) {
        return false;
      }

      if (selectedResult && attempt.result !== selectedResult) {
        return false;
      }

      if (selectedStatus && attempt.status !== selectedStatus) {
        return false;
      }

      if (minPercentage !== "" && attempt.percentage < Number(minPercentage)) {
        return false;
      }

      if (maxPercentage !== "" && attempt.percentage > Number(maxPercentage)) {
        return false;
      }

      return true;
    });
  }, [
    attempts,
    selectedStudent,
    selectedResult,
    selectedStatus,
    minPercentage,
    maxPercentage,
  ]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <p className="text-gray-500 text-lg">Loading quiz attempts...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg">
        {error?.data?.message || "Failed to load quiz attempts"}
      </div>
    );
  }

  

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Quiz Attempts</h1>

        <p className="text-gray-500 mt-1">
          View all quizzes attempted by students
        </p>
      </div>
      
      <div className="mb-6 bg-white border rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Student */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Student
            </label>

            <select
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">All Students</option>

              {students.map((student) => (
                <option key={student._id} value={student._id}>
                  {student.name}
                  {student.studentId ? ` (${student.studentId})` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Result */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Result
            </label>

            <select
              value={selectedResult}
              onChange={(e) => setSelectedResult(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">All Results</option>
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="inProgress">In Progress</option>
              <option value="timeout">Timeout</option>
              <option value="abandoned">Abandoned</option>
            </select>
          </div>

          {/* Minimum Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min %
            </label>

            <input
              type="number"
              min="0"
              max="100"
              value={minPercentage}
              onChange={(e) => setMinPercentage(e.target.value)}
              placeholder="0"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          {/* Maximum Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max %
            </label>

            <input
              type="number"
              min="0"
              max="100"
              value={maxPercentage}
              onChange={(e) => setMaxPercentage(e.target.value)}
              placeholder="100"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing <strong>{filteredAttempts.length}</strong> of{" "}
            <strong>{attempts.length}</strong> attempts
          </p>

          <button
            onClick={() => {
              setSelectedStudent("");
              setSelectedResult("");
              setSelectedStatus("");
              setMinPercentage("");
              setMaxPercentage("");
            }}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
          >
            Clear Filters
          </button>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Student
                </th>

                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Quiz
                </th>

                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                  Score
                </th>

                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                  Percentage
                </th>

                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                  Result
                </th>

                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                  Status
                </th>

                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                  Action
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {filteredAttempts.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-10 text-center text-gray-500"
                  >
                    No quiz attempts found.
                  </td>
                </tr>
              ) : (
                filteredAttempts.map((attempt) => (
                  <tr key={attempt._id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">
                          {attempt.student?.name || "Unknown"}
                        </p>

                        <p className="text-sm text-gray-500">
                          {attempt.student?.studentId}
                        </p>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-gray-700">
                      {attempt.quiz?.title || "Deleted Quiz"}
                    </td>

                    <td className="px-6 py-4 text-center font-medium">
                      {attempt.obtainedMarks}/{attempt.totalMarks}
                    </td>

                    <td className="px-6 py-4 text-center">
                      {attempt.percentage}%
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span
                        className={`
                          px-3 py-1 rounded-full text-xs font-semibold
                          ${
                            attempt.result === "pass"
                              ? "bg-green-100 text-green-700"
                              : attempt.result === "fail"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                          }
                        `}
                      >
                        {attempt.result}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                        {attempt.status}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() =>
                          navigate(`/admin/quiz-attempts/${attempt._id}`)
                        }
                        className="
                          bg-blue-600
                          hover:bg-blue-700
                          text-white
                          px-4
                          py-2
                          rounded-lg
                          text-sm
                          font-medium
                          transition
                        "
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminQuizAttempts;
